import { AimOutlined, PlusCircleOutlined } from '@ant-design/icons'
import { useRequest } from 'ahooks'
import { Button, Typography } from 'antd'
import { useAtom } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  DepartmentName,
  MosaicComponent,
  RelationsCreator,
  RelationsDeleter,
  UserAvatar,
  UserName,
  getPointPermission,
} from '@/components'
import { okrCyclesAtom } from '@/modules/okr'
import { PAGE_RESOURCE_POINTS_ENUM, PpOKR, Resource, ResourceType } from '@/schemas'

const { Title } = Typography

interface ResourceOKRProps {
  resource: Resource
  isSummaryPage?: boolean
}

export const ResourceOKR: React.FC<ResourceOKRProps> = ({ resource, isSummaryPage = false }) => {
  const { selfId, type } = resource
  const { canRead, canCreate, canDelete } = getPointPermission(
    PAGE_RESOURCE_POINTS_ENUM.概况_关联OKR,
    resource.permissions
  )
  const { t } = useTranslation()
  const [okrCycles] = useAtom(okrCyclesAtom)
  const { data, refresh } = useRequest<PpOKR[]>(
    {
      url: '/api/relations/getPpRelations',
      params: {
        selfId,
        rootTypes: [ResourceType.OBJECTIVE],
        depth: 0,
        fetchType: 2,
        // 不需要 cycleId, 获取该项目关联的所以 OKR
      },
    },
    {
      refreshDeps: [selfId],
      manual: !canRead,
    }
  )

  let content = <Typography.Text type='secondary'>{t('objective.nodata')}</Typography.Text>

  if (data && data.length > 0) {
    const objectives = data.map(({ parentObjective: o }) => (
      <div key={o.objectiveId} className='flex items-center group'>
        <UserAvatar className='mr-8px' email={o.email} />
        <div className='text-xs'>
          <Typography.Text className='block'>{o.name}</Typography.Text>
          <Typography.Text type='secondary' className='block'>
            <UserName email={o.email} />
            <span> - </span>
            <DepartmentName canRead={true} departmentId={o.departmentId} />
            <span className='ml-5px inline-block'>{okrCycles.find((i) => i.cycleId === o.cycleId)?.name}</span>
            {canDelete && (
              <RelationsDeleter
                parentSelfId={o.objectiveId}
                parentType={ResourceType.OBJECTIVE}
                selfId={selfId}
                type={type}
                onSuccess={refresh}
                isSummaryPage={isSummaryPage}
              >
                <Button type='link' size='small' className='text-xs opacity-0 group-hover:opacity-100'>
                  {t('remove')}
                </Button>
              </RelationsDeleter>
            )}
          </Typography.Text>
        </div>
      </div>
    ))

    content = <div className='space-y-12px'>{objectives}</div>
  }

  return (
    <div>
      <Title level={4} className='flex items-center'>
        <AimOutlined className='mr-8px' />
        <span>{t('associate okr')}</span>
        {canCreate && (
          <RelationsCreator
            direction='up'
            rootSelfId={selfId}
            selfId={selfId}
            type={type}
            targetType={ResourceType.OBJECTIVE}
            onSuccess={refresh}
            disabledSelfIds={(data || []).map(({ parentObjective: objective }) => objective.objectiveId)}
          >
            <PlusCircleOutlined className='ml-12px' style={{ fontSize: 14 }} />
          </RelationsCreator>
        )}
      </Title>
      {canRead ? content : <MosaicComponent />}
    </div>
  )
}
